// AdminLogin — admin sign-in page at /login.
//
// Phase 6 UI overhaul (2026-05-16): kept the dark slate/purple
// gradient bg (this page's signature look) but lifted the form
// surface + input fields to the unified arena/neon design tokens
// used everywhere else in the SPA. Fuchsia focus rings switched to
// the admin's violet identity. Inputs got the `ring-1 ring-inset`
// pattern. The fuchsia→purple submit gradient stays — it's the
// brand-shaped CTA the user already associates with logging in.

import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import Icon from '@baaligames/shared-ui/components/Icon';
import BaaliLogo from '@baaligames/shared-ui/admin/components/BaaliLogo';

export default function AdminLogin() {
  const { admin, login, loading } = useAdminAuth();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // If there's already a live admin session (refresh cookie accepted),
  // bounce to the dashboard — no need to show the login form.
  useEffect(() => {
    if (!loading && admin) nav('/', { replace: true });
  }, [admin, loading, nav]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      // expected_role='admin' tells the backend this URL only accepts
      // platform admins. Agents typing their credentials here
      // will be rejected with a generic 401 — they need /agent/login.
      await login(identifier, password, 'admin');
      nav('/', { replace: true });
    } catch (e) {
      setError((e as ApiError).message || 'Login failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen min-h-dvh grid place-items-center px-4
                 bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 text-neon-100"
      style={{
        paddingTop:    'max(1rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
        paddingLeft:   'max(1rem, env(safe-area-inset-left))',
        paddingRight:  'max(1rem, env(safe-area-inset-right))',
      }}>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 220, damping: 24 }}
        className="w-full max-w-sm"
      >
        <div className="text-center mb-6 flex flex-col items-center">
          <div className="mb-3">
            <BaaliLogo role="admin" wordmark={false} size={64} />
          </div>
          <h1 className="text-2xl font-bold text-white">Baali Admin</h1>
          <p className="text-sm text-neon-200/60">Sign in to manage the platform</p>
        </div>
        <form onSubmit={submit}
              className="rounded-2xl bg-arena-800/70 backdrop-blur
                         ring-1 ring-inset ring-arena-600/40
                         p-5 space-y-4 shadow-2xl">
          <div>
            <label className="block text-xs font-semibold text-neon-200/70 mb-1.5">
              Username or email
            </label>
            <div className="relative">
              <Icon name="person" size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neon-200/45" />
              <input
                className="w-full rounded-xl bg-arena-900/60
                           ring-1 ring-inset ring-arena-600/50
                           pl-10 pr-3 py-2.5 text-base text-white
                           placeholder-neon-200/40
                           focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                style={{ fontSize: 16 }}
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neon-200/70 mb-1.5">Password</label>
            <div className="relative">
              <Icon name="lock" size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-neon-200/45" />
              <input
                type="password"
                className="w-full rounded-xl bg-arena-900/60
                           ring-1 ring-inset ring-arena-600/50
                           pl-10 pr-3 py-2.5 text-base text-white
                           placeholder-neon-200/40
                           focus:outline-none focus:ring-2 focus:ring-violet-400/60"
                style={{ fontSize: 16 }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          </div>
          {error && (
            <div className="rounded-lg bg-rose-500/10
                            ring-1 ring-inset ring-rose-400/40
                            text-rose-200 text-sm px-3 py-2">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl
                       bg-gradient-to-r from-fuchsia-600 to-purple-700
                       hover:from-fuchsia-500 hover:to-purple-600
                       text-white font-semibold py-2.5
                       shadow-[0_4px_18px_-2px_rgba(139,92,246,0.45)]
                       transition active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <Icon name="progress_activity" size={18} className="animate-spin" /> : <Icon name="login" size={18} />}
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
          <p className="text-center text-[11px] text-neon-200/55 pt-1">
            Restricted area. Credential events are logged.
          </p>
        </form>
      </motion.div>
    </div>
  );
}
